import React, { useState } from 'react';
import { X, PackageCheck, Loader2, CheckSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import purchaseOrderService from '../../../../services/purchaseOrderService';

// Resolve received quantity regardless of backend field name
const resolvedReceived = (li) =>
  li.receivedQuantity ?? li.receivedQty ?? li.received ?? 0;

// Resolve line items regardless of backend field name
const resolveLineItems = (po) =>
  po?.lineItems ?? po?.items ?? po?.orderItems ?? [];

export default function ReceivePOModal({ po, onClose, onSave }) {
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes,       setNotes]       = useState('');
  const [saving,      setSaving]      = useState(false);

  // Build receivable rows — only lines with remaining qty
  const [receiveItems, setReceiveItems] = useState(() =>
    resolveLineItems(po)
      .map((li) => {
        const prevReceived = resolvedReceived(li);
        const remaining    = (li.quantity ?? 0) - prevReceived;
        return {
          lineItemId:         li.id,
          itemName:           li.itemName ?? li.name ?? '—',
          uom:                li.uom ?? li.unit ?? '',
          ordered:            li.quantity ?? 0,
          previouslyReceived: prevReceived,
          remaining,
          quantity:           '',
        };
      })
      .filter((li) => li.remaining > 0)
  );

  const updateQty = (idx, value) => {
    setReceiveItems((prev) => {
      const updated = [...prev];
      const clamped = value === ''
        ? ''
        : Math.min(Math.max(0, Number(value)), updated[idx].remaining);
      updated[idx] = { ...updated[idx], quantity: clamped === '' ? '' : clamped };
      return updated;
    });
  };

  const receiveAll = () =>
    setReceiveItems((prev) => prev.map((li) => ({ ...li, quantity: li.remaining })));

  const totalReceiving = receiveItems.reduce((sum, li) => sum + (Number(li.quantity) || 0), 0);
  const totalRemaining = receiveItems.reduce((sum, li) => sum + li.remaining, 0);

const handleSave = async () => {
  const itemsToReceive = receiveItems.filter((li) => Number(li.quantity) > 0);
  if (itemsToReceive.length === 0) {
    toast.error('Enter a quantity for at least one item');
    return;
  }
  if (!receiveDate) {
    toast.error('Please select a receipt date');
    return;
  }
  setSaving(true);
  try {
   await purchaseOrderService.receivePO(
  po.id,
  itemsToReceive.map((li) => ({
    poItemId:    li.lineItemId,   // ✅ was lineItemId
    receivedQty: Number(li.quantity), // ✅ was quantity
    notes:       '',
  })),
  notes,
  receiveDate,
);
    toast.success('Items received successfully');
    onSave();
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'Failed to receive items';
    toast.error(msg);
  } finally {
    setSaving(false);
  }
};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-violet-600" />
            <div>
              <h2 className="text-base font-bold text-slate-800">Receive Items</h2>
              <p className="text-[11px] text-slate-500">{po?.poNumber} — {po?.supplierName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Date */}
          <div>
            <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
              Date Received
            </label>
            <input
              type="date"
              value={receiveDate}
              onChange={(e) => setReceiveDate(e.target.value)}
              className="w-full max-w-xs px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
            />
          </div>

          {/* Receive All */}
          <div className="flex justify-end">
            <button
              onClick={receiveAll}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              Receive All
            </button>
          </div>

          {/* Items Table */}
          {receiveItems.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-xs text-slate-500">All items have been fully received.</p>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left   px-3 py-2 font-semibold text-slate-600">Item</th>
                    <th className="text-center  px-3 py-2 font-semibold text-slate-600">UOM</th>
                    <th className="text-center  px-3 py-2 font-semibold text-slate-600">Ordered</th>
                    <th className="text-center  px-3 py-2 font-semibold text-slate-600">Previously Received</th>
                    <th className="text-center  px-3 py-2 font-semibold text-slate-600">Receive Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {receiveItems.map((li, idx) => (
                    <tr key={li.lineItemId ?? idx} className="hover:bg-slate-50/40">
                      <td className="px-3 py-2 text-slate-700 font-medium">{li.itemName}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{li.uom || '—'}</td>
                      <td className="px-3 py-2 text-center text-slate-600">{li.ordered}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{li.previouslyReceived}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          max={li.remaining}
                          value={li.quantity}
                          onChange={(e) => updateQty(idx, e.target.value)}
                          className="w-20 mx-auto block text-center px-2 py-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-violet-400"
                          placeholder={`max ${li.remaining}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional receiving notes..."
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 resize-none"
            />
          </div>

          {/* Summary */}
          {totalReceiving > 0 && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2">
              <p className="text-xs text-violet-700 font-medium">
                Receiving {totalReceiving} of {totalRemaining} remaining items
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || totalReceiving === 0}
            className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Confirm Receipt
          </button>
        </div>
      </div>
    </div>
  );
}